import type {
  GeneralLedgerAccount,
  GeneralLedgerAccountV2,
  GeneralLedgerAccountV2Type,
} from "src/interfaces/generalLedgerAccount.interface";

export const GeneralLedgerAccountTypeToLabelMaps = {
  assets: {
    label: "Assets",
    types: {
      "bank-account-cash": "Bank Account / Cash",
      "money-owed-to-you": "Money Owed to You",
      inventory: "Inventory",
      "property-plant-equipment": "Property, Plant, Equipment",
      "depreciation-and-amortization": "Depreciation and Amortization",
      "other-short-term-asset": "Other Short-Term Asset",
      "other-long-term-asset": "Other Long-Term Asset",
    },
  },
  liabilities: {
    label: "Liabilities",
    types: {
      "credit-card": "Credit Card",
      "loan-line-of-credit": "Loan / Line of Credit",
      "money-owed-to-vendors": "Money Owed to Vendors",
      "other-short-term-liability": "Other Short-Term Liability",
      "other-long-term-liability": "Other Long-Term Liability",
    },
  },
  income: {
    label: "Income",
    types: {
      income: "Income",
      discounts: "Discounts",
      "other-income": "Other Income",
    },
  },
  expenses: {
    label: "Expenses",
    types: {
      operating: "Operating",
      "cost-of-goods-sold": "Cost of Goods Sold",
      "payment-processing": "Payment Processing",
      payroll: "Payroll",
    },
  },
  equity: {
    label: "Equity",
    types: {
      "owner-contributions-draws": "Owner Contributions/Draws",
      "retained-earnings": "Retained Earnings",
    },
  },
};

export function generalLedgerAccountTypeToLabel(
  type: GeneralLedgerAccountV2Type
): [string, string] {
  for (const [key, value] of Object.entries(
    GeneralLedgerAccountTypeToLabelMaps
  )) {
    if (key === type[0]) {
      for (const [k, v] of Object.entries(value.types)) {
        if (k === type[1]) return [value.label, v];
      }

      return [value.label, type[1]];
    }
  }

  return type;
}

export function mapGeneralLedgerAccount(
  account: GeneralLedgerAccount
): GeneralLedgerAccountV2 {
  if (account.type === "Other Business Costs") {
    return {
      ...account,
      type: ["expenses", "operating"],
    };
  }

  if (account.type === "Other COGS") {
    return {
      ...account,
      type: ["expenses", "cost-of-goods-sold"],
    };
  }

  throw new Error(`Can't map ${account.type} (${account.id})`);
}

export const DefaultAccounts: GeneralLedgerAccountV2[] = [
  {
    id: "shipping-supplies",
    name: "Shipping Supplies",
    type: ["expenses", "cost-of-goods-sold"],
    default: true,
  },
  {
    id: "shipping-fees",
    name: "Shipping Fees",
    type: ["expenses", "cost-of-goods-sold"],
    default: true,
  },
  {
    id: "sourcing-mileage-transportation",
    name: "Sourcing Mileage/Transportation",
    type: ["expenses", "cost-of-goods-sold"],
    default: true,
  },
  {
    id: "listing-platform-fees",
    name: "Listing/Platform Fees",
    type: ["expenses", "cost-of-goods-sold"],
    default: true,
  },
  {
    id: "office-supplies",
    name: "Office Supplies",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "donations",
    name: "Donations",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "non-sourcing-mileage-transportation",
    name: "Non-Sourcing Mileage/Transportation",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "travel",
    name: "Travel",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "cell-phone",
    name: "Cell Phone",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "internet",
    name: "Internet",
    type: ["expenses", "operating"],
    default: true,
  },
  { id: "rent", name: "Rent", type: ["expenses", "operating"], default: true },
  {
    id: "utilities",
    name: "Utilities",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "bank-fees",
    name: "Bank Fees",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "subscriptions-dues",
    name: "Subscriptions/Dues",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "equipment",
    name: "Equipment",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "car-expenses",
    name: "Car Expenses",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "contract-work",
    name: "Contract Work",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "ask-accountant",
    name: "Ask Accountant",
    type: ["expenses", "operating"],
    default: true,
  },
  {
    id: "inventory-purchases",
    name: "Inventory Purchases",
    type: ["expenses", "cost-of-goods-sold"],
    default: true,
    // XXX: 2025-05-16: we are enabling this account because the categorization process doesnt distinguish between cash and accrual users
    //                  this ended up in showing an empty account in the 'Categorize Expenses' for that type of users
    // inventoryType: "cash",
  },
  {
    id: "transfers",
    name: "Balance Transfer",
    type: ["assets", "other-short-term-asset"],
    default: true,
    system: true,
  },
  {
    id: "unassigned",
    name: "Unassigned",
    default: true,
    system: true,
    type: ["assets", "other-short-term-asset"],
  },
  {
    id: "sales-tax-payable",
    name: "Sales Tax Payable",
    default: true,
    system: true,
    type: ["liabilities", "sales-tax-payable"],
  },
  {
    id: "uncategorized",
    name: "Uncategorized",
    default: true,
    system: true,
    type: ["expenses", "operating"],
  },
];

export const TransfersAccountId = "transfers";
export const TransfersAccountName =
  DefaultAccounts.find((a) => a.id === TransfersAccountId)?.name ??
  "Balance Transfer";

export function getGeneralLedgerAccountTypeByName(
  accounts: GeneralLedgerAccountV2[],
  name: string
): GeneralLedgerAccountV2Type | undefined {
  for (const account of accounts) {
    if (account.name === name) return account.type;
  }

  if (
    name === "Transaction Fees" ||
    name === "Shipping Costs" ||
    name === "Purchase Price"
  )
    return ["expenses", "cost-of-goods-sold"] as GeneralLedgerAccountV2Type;
}

export function isBankTransfer(type?: [string, string]): boolean {
  return (
    !!type?.[1] &&
    ["bank-account-cash", "credit-card", "loan-line-of-credit"].includes(
      type[1]
    )
  );
}

export const GeneralLedgerAccountTypeOrder: GeneralLedgerAccountV2Type[0][] = [
  "expenses",
  "income",
  "assets",
  "liabilities",
  "equity",
];

export const GeneralLedgerAccountTypeOrderById =
  GeneralLedgerAccountTypeOrder.reduce((order, key, index) => {
    order[key] = index;
    return order;
  }, {} as Record<GeneralLedgerAccountV2Type[0], number>);
